/**
 * KRA Photo Journal – TagEditor (Admin only)
 *
 * Mobile-first tag editor:
 * - All existing tags shown as large tappable chips (toggle on/off)
 * - "Thêm tag khác…" button opens a simple text prompt
 * - No small text inputs → no iOS zoom issue
 * - Font-size 16px on any input to prevent iOS auto-zoom
 */

import React, { useState, useCallback } from "react";
import type { PhotoRecord } from "@kra/photo-journal-shared";
import { TAG_GROUPS } from "../lib/tagHelpers.js";
import { patchPhoto, type TagOptions } from "../lib/api.js";
import { cn } from "../lib/utils.js";

interface TagEditorProps {
  photo: PhotoRecord;
  allPhotos: PhotoRecord[];
  /** Options from Airtable field schema — always available, even for unused tags */
  tagOptions?: TagOptions;
  onSaved: (updated: Partial<PhotoRecord>) => void;
}

// Map TAG_GROUPS key → Airtable field name
const GROUP_TO_FIELD: Record<string, keyof TagOptions> = {
  kimonoTags:      "kimono_tag",
  shootTags:       "shoot_tag",
  seasonTags:      "season_tag",
  sceneTags:       "scene_tag",
  kimonoColorTags: "kimono_color_tag",
};

type TagState = Record<string, string[]>;

export function TagEditor({ photo, allPhotos, tagOptions, onSaved }: TagEditorProps) {
  const [tagState, setTagState] = useState<TagState>(() => ({
    kimonoTags: [...(photo.kimonoTags ?? [])],
    shootTags:  [...(photo.shootTags  ?? [])],
    seasonTags: [...(photo.seasonTags ?? [])],
    sceneTags:  [...(photo.sceneTags  ?? [])],
  }));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Per-group: show custom input
  const [addingFor, setAddingFor] = useState<string | null>(null);
  const [customInput, setCustomInput] = useState("");

  /** All options = Airtable schema choices + tags used in other records + currently selected */
  function getOptions(groupKey: string): string[] {
    const set = new Set<string>();
    // 1. Schema-defined options (from Airtable field definition)
    const fieldName = GROUP_TO_FIELD[groupKey];
    if (fieldName && tagOptions) {
      for (const t of tagOptions[fieldName] ?? []) if (t.trim()) set.add(t.trim());
    }
    // 2. Tags actually used in loaded records
    for (const p of allPhotos) {
      const tags = (p as unknown as Record<string, string[]>)[groupKey] ?? [];
      for (const t of tags) if (t.trim()) set.add(t.trim());
    }
    // 3. Currently selected (may include newly-typed custom tags)
    for (const t of tagState[groupKey] ?? []) if (t.trim()) set.add(t.trim());
    return Array.from(set).sort((a, b) => a.localeCompare(b, "vi"));
  }

  function toggleTag(groupKey: string, value: string) {
    setTagState((prev) => {
      const current = prev[groupKey] ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [groupKey]: next };
    });
    setSaved(false);
    setSaveError(null);
  }

  function confirmCustom(groupKey: string) {
    const value = customInput.trim();
    if (value) {
      setTagState((prev) => {
        const current = prev[groupKey] ?? [];
        if (current.includes(value)) return prev;
        return { ...prev, [groupKey]: [...current, value] };
      });
      setSaved(false);
    }
    setCustomInput("");
    setAddingFor(null);
  }

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await patchPhoto(photo.id, {
        tags: {
          kimono_tag:       tagState["kimonoTags"]      ?? [],
          kimono_color_tag: tagState["kimonoColorTags"] ?? [],
          season_tag:       tagState["seasonTags"]      ?? [],
          shoot_tag:        tagState["shootTags"]       ?? [],
          scene_tag:        tagState["sceneTags"]       ?? [],
        },
      });
      setSaved(true);
      onSaved({
        kimonoTags:      tagState["kimonoTags"]      ?? [],
        kimonoColorTags: tagState["kimonoColorTags"] ?? [],
        seasonTags:      tagState["seasonTags"]      ?? [],
        shootTags:       tagState["shootTags"]       ?? [],
        sceneTags:       tagState["sceneTags"]       ?? [],
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Lưu thất bại");
    } finally {
      setSaving(false);
    }
  }, [photo.id, tagState, onSaved]);

  const isDirty =
    JSON.stringify(tagState["kimonoTags"])      !== JSON.stringify(photo.kimonoTags      ?? []) ||
    JSON.stringify(tagState["kimonoColorTags"]) !== JSON.stringify(photo.kimonoColorTags ?? []) ||
    JSON.stringify(tagState["seasonTags"])      !== JSON.stringify(photo.seasonTags      ?? []) ||
    JSON.stringify(tagState["shootTags"])       !== JSON.stringify(photo.shootTags       ?? []) ||
    JSON.stringify(tagState["sceneTags"])       !== JSON.stringify(photo.sceneTags       ?? []);

  return (
    <div className="rounded-[24px] border border-surface-2 bg-white/72 px-4 py-4 shadow-[inset_0_0_0_1px_rgba(26,25,23,0.02)] md:px-5 md:py-5">
      <div className="mb-4 flex items-center gap-2">
        <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth={2.2} strokeLinecap="round" className="text-ink-2">
          <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
        </svg>
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-2">Chỉnh tag</span>
      </div>

      <div className="space-y-5">
        {TAG_GROUPS.map((group) => {
        const options = getOptions(group.key);
        const selected = tagState[group.key] ?? [];
        const isAddingThis = addingFor === group.key;

        return (
          <div key={group.key} className="flex flex-col gap-3">
            {/* Group label */}
            <span className="text-[12px] font-medium text-ink-1">
              {group.emoji} {group.label}
            </span>

            {/* Tag chips — larger for touch */}
            <div className="flex flex-wrap gap-2.5">
              {options.map((opt) => {
                const isSel = selected.includes(opt);
                return (
                  <button
                    key={opt}
                    onClick={() => toggleTag(group.key, opt)}
                    className={cn(
                      "flex min-h-[38px] items-center rounded-full px-3 py-1.5 text-[11px] font-medium transition-all duration-150",
                      isSel
                        ? "bg-[#efe5d7] text-ink-0 shadow-sm"
                        : "border border-[#ebe5db] bg-[#f6f2ea] text-ink-2 hover:bg-[#efe9df] hover:text-ink-1"
                    )}
                  >
                    {isSel && <span className="mr-1 text-[10px] opacity-70">✓</span>}
                    {opt}
                  </button>
                );
              })}

              {/* Add custom tag button / input */}
              {isAddingThis ? (
                <div className="mt-1 flex w-full items-center gap-2">
                  <input
                    autoFocus
                    type="text"
                    value={customInput}
                    onChange={(e) => setCustomInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); confirmCustom(group.key); }
                      if (e.key === "Escape") { setAddingFor(null); setCustomInput(""); }
                    }}
                    placeholder={`Tag ${group.label} mới…`}
                    // font-size 16px prevents iOS auto-zoom
                    style={{ fontSize: "16px" }}
                    className="flex-1 rounded-2xl border border-surface-2 bg-white px-3 py-2 text-ink-1 placeholder:text-ink-3 focus:border-ink-3 focus:outline-none transition-colors"
                  />
                  <button
                    onClick={() => confirmCustom(group.key)}
                    className="rounded-2xl bg-surface-2 px-3 py-2 text-[11px] font-medium text-ink-1 transition-colors hover:bg-surface-3"
                  >
                    Thêm
                  </button>
                  <button
                    onClick={() => { setAddingFor(null); setCustomInput(""); }}
                    className="rounded-2xl bg-surface-1 px-3 py-2 text-[11px] text-ink-3 transition-colors hover:bg-surface-2"
                  >
                    Hủy
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { setAddingFor(group.key); setCustomInput(""); }}
                  className="flex min-h-[38px] items-center rounded-full border border-dashed border-[#d9d1c2] px-3 py-1.5 text-[11px] font-medium text-ink-2 transition-colors hover:border-[#b9ae99] hover:text-ink-1"
                >
                  + Thêm tag khác
                </button>
              )}
            </div>
          </div>
        );
        })}
      </div>

      {/* Save button */}
      <button
        onClick={handleSave}
        disabled={saving || (!isDirty && !saved)}
        className={cn(
          "mt-5 w-full rounded-[18px] py-3 text-sm font-semibold transition-all duration-150",
          saving
            ? "bg-surface-2 text-ink-3 cursor-wait"
            : saved && !isDirty
            ? "bg-[#e6efe4] text-status-ready cursor-default"
            : isDirty
            ? "bg-ink-0 text-surface-0 hover:bg-[#23211e] active:bg-[#11100f]"
            : "bg-surface-1 text-ink-3 cursor-not-allowed"
        )}
      >
        {saving ? "Đang lưu…" : saved && !isDirty ? "✓ Đã lưu" : "Lưu tags"}
      </button>

      {saveError && (
        <p className="mt-3 text-center text-[11px] text-status-failed">{saveError}</p>
      )}
    </div>
  );
}
